import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from './DatePicker';

// ── Yardımcılar ──────────────────────────────────────────────
function bugunStr() {
  const d = new Date();
  return [String(d.getDate()).padStart(2, '0'), String(d.getMonth() + 1).padStart(2, '0'), d.getFullYear()].join('.');
}
function saatStr() {
  const d = new Date();
  return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
}

function toTitleCase(str) {
  return str.toLocaleLowerCase('tr-TR').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

// ── ConfirmModal ──────────────────────────────────────────────
export function ConfirmModal({ mesaj, onOnayla, onIptal }) {
  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="px-6 py-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <span className="text-red-600 text-lg">⚠</span>
            </div>
            <h3 className="text-sm font-bold text-gray-800">Emin misiniz?</h3>
          </div>
          <p className="text-sm text-gray-600">{mesaj}</p>
        </div>
        <div className="flex border-t border-gray-100">
          <button onClick={onIptal} className="flex-1 py-3 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors border-r border-gray-100">İptal</button>
          <button onClick={onOnayla} className="flex-1 py-3 text-sm font-bold text-red-600 hover:bg-red-50 transition-colors">Sil</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── SearchableSelect — portal + akıllı konum ─────────────────
export function SearchableSelect({ value, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const [ara, setAra] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, openUp: false });
  const triggerRef = useRef(null);

  useEffect(() => {
    const h = e => { if (triggerRef.current && !triggerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  function handleOpen() {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownH = 240;
      setPos(spaceBelow < dropdownH
        ? { openUp: true, bottom: window.innerHeight - rect.top + window.scrollY, top: 'auto', left: rect.left + window.scrollX, width: rect.width }
        : { openUp: false, top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX, width: rect.width }
      );
    }
    setOpen(p => !p);
    setAra('');
  }

  const filtered = options.filter(o => o.toLowerCase().includes(ara.toLowerCase()));

  return (
    <div ref={triggerRef} className="relative">
      <div onClick={handleOpen}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white cursor-pointer flex items-center justify-between">
        <span className={value ? 'text-gray-800' : 'text-gray-400'}>{value || placeholder || 'Seçiniz'}</span>
        <span className="text-gray-400 text-xs ml-2">{open ? '▲' : '▼'}</span>
      </div>
      {open && createPortal(
        <div style={{ position: 'absolute', top: pos.openUp ? 'auto' : pos.top, bottom: pos.openUp ? pos.bottom : 'auto', left: pos.left, width: pos.width, zIndex: 9999 }}
          className="bg-white border border-gray-300 rounded-lg shadow-2xl overflow-hidden"
          onMouseDown={e => e.stopPropagation()}>
          <input autoFocus value={ara} onChange={e => setAra(e.target.value)}
            className="w-full px-3 py-2 text-sm border-b border-gray-200 focus:outline-none" placeholder="Ara..." />
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0
              ? <div className="px-3 py-2 text-sm text-gray-400">Sonuç yok</div>
              : filtered.map(o => (
                <div key={o} onClick={() => { onChange(o); setOpen(false); }}
                  className={'px-3 py-2 text-sm cursor-pointer ' + (o === value ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 text-gray-800')}>
                  {o}
                </div>
              ))
            }
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ── GelirEkleModal ────────────────────────────────────────────
export function GelirEkleModal({ onClose, onKaydet, onSil, mevcutKayit, gelirGruplari = [], personeller = [], odemeTipleri = [], bankalar = [], paraBirimleri = [], dovizKurlari = {} }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  const isDetay = !!mevcutKayit;
  const [confirmAc, setConfirmAc] = useState(false);

  const odemeTipListesi = odemeTipleri.length ? odemeTipleri.map(o => typeof o === 'object' ? o.ad : o) : ['BANKA HAVALE', 'NAKİT', 'KREDİ KARTI', 'ÇEK'];
  const bankaListesi = bankalar.length ? bankalar.map(b => typeof b === 'object' ? b.ad : b) : [];
  const paraBirimiListesi = paraBirimleri.length ? paraBirimleri.map(p => typeof p === 'object' ? (p.tcmbKod || p.ad) : p) : ['TL', 'EURO', 'USD'];

  const ilkGrup = gelirGruplari[0] || {};
  const [gelirGrubuId, setGelirGrubuId] = useState(mevcutKayit?.gelirGrubuId || ilkGrup.gelirGrubuId || 0);
  const [ad, setAd] = useState(mevcutKayit?.ad || '');
  const [tutar, setTutar] = useState(mevcutKayit?.tutar || 0);
  const [paraBirimi, setParaBirimi] = useState(mevcutKayit?.paraBirimi || paraBirimiListesi.find(p => p === 'TRY' || p === 'TL') || paraBirimiListesi[0] || 'TL');
  const [odemeTip, setOdemeTip] = useState(mevcutKayit?.odemeTip || (odemeTipListesi[0] || 'NAKİT'));
  const [banka, setBanka] = useState(mevcutKayit?.banka || '');
  const [personelId, setPersonelId] = useState(mevcutKayit?.personelId || null);
  const [tarih, setTarih] = useState(mevcutKayit?.tarih || bugunStr());
  const [saat, setSaat] = useState(mevcutKayit?.saat || saatStr());

  const kurDegeri = dovizKurlari[paraBirimi] || dovizKurlari['TL'] || 1;
  const tlKarsıligi = (parseFloat(tutar) || 0) * kurDegeri;

  const secilenGrup = gelirGruplari.find(g => g.gelirGrubuId === gelirGrubuId);
  const grupOptions = gelirGruplari.map(g => g.ad);
  const personelOptions = ['— Seçiniz —', ...personeller.map(p => p.adSoyad)];

  function handleGrupSec(v) { const g = gelirGruplari.find(g => g.ad === v); if (g) setGelirGrubuId(g.gelirGrubuId); }
  function handlePersonelSec(v) {
    if (v === '— Seçiniz —') { setPersonelId(null); return; }
    const p = personeller.find(p => p.adSoyad === v); if (p) setPersonelId(p.personelId);
  }

  async function kaydet() {
    if (!gelirGrubuId) { alert('Lütfen grup seçiniz.'); return; }
    if (!tutar || parseFloat(tutar) <= 0) { alert('Tutar giriniz.'); return; }
    try {
      await onKaydet({ id: mevcutKayit?.id, kasaGelirId: mevcutKayit?.kasaGelirId, gelirGrubuId, ad, tutar: parseFloat(tutar), paraBirimi, kurDegeri, odemeTip, banka, personelId, tarih, saat });
      onClose();
    } catch (err) {
      alert(err?.message || 'Kaydedilemedi.');
    }
  }

  const inputCls = 'w-full border-2 border-gray-100 rounded-2xl px-5 py-3.5 text-xs font-bold text-gray-800 focus:outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50 bg-white transition-all';
  const labelCls = 'text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1 leading-none';
  const bankaGerekli = odemeTip.localeCompare('NAKİT', 'tr', { sensitivity: 'base' }) !== 0 && bankaListesi.length > 0;

  return createPortal(
    <div className="fixed inset-0 bg-black/60 z-[9999] flex items-end sm:items-center justify-center sm:p-4 backdrop-blur-md animate-fadeIn" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-xl rounded-t-[2.5rem] sm:rounded-[2.5rem] shadow-2xl flex flex-col border border-white/20 animate-scaleIn overflow-hidden" style={{ maxHeight: '95dvh' }} onClick={e => e.stopPropagation()}>
        <div className="relative flex items-center justify-between px-8 py-7 bg-gradient-to-r from-emerald-500 to-teal-600 text-left leading-none flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center text-2xl border border-white/30">💰</div>
            <div>
              <h2 className="text-white font-black text-lg tracking-tight uppercase leading-none">{isDetay ? 'Gelir Düzenle' : 'Gelir Ekle'}</h2>
              <p className="text-white/70 text-[10px] font-bold uppercase tracking-widest mt-1.5 opacity-80 leading-none">Finansal gelir kaydı yönetimi</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white w-10 h-10 flex items-center justify-center rounded-xl hover:bg-white/10 transition-all font-bold">✕</button>
        </div>

        <div className="px-8 py-8 space-y-6 overflow-y-auto flex-1 no-scrollbar text-left leading-none">
          <div>
            <label className={labelCls}>GRUP / KATEGORİ *</label>
            <SearchableSelect value={secilenGrup?.ad || ''} onChange={handleGrupSec} options={grupOptions} placeholder="Grup seçiniz" />
          </div>
          <div>
            <label className={labelCls}>AÇIKLAMA</label>
            <input value={ad} onChange={e => setAd(toTitleCase(e.target.value))} placeholder="Gelir açıklaması giriniz..." className={inputCls} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className={labelCls}>TUTAR</label>
              <div className="relative">
                <input type="number" value={tutar} onChange={e => setTutar(e.target.value)} className={inputCls + ' pr-20'} />
                <select value={paraBirimi} onChange={e => setParaBirimi(e.target.value)} className="absolute right-2 top-2 bottom-2 rounded-xl border-none bg-gray-50 text-[10px] font-black px-2 focus:ring-0">
                  {paraBirimiListesi.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>ÖDEME TİPİ</label>
              <select value={odemeTip} onChange={e => setOdemeTip(e.target.value)} className={inputCls + ' cursor-pointer'}>
                {odemeTipListesi.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>
          {paraBirimi !== 'TL' && paraBirimi !== 'TRY' && kurDegeri > 0 && (
            <div className="bg-blue-50 border-2 border-blue-100 rounded-2xl p-4 flex justify-between items-center animate-fadeIn">
              <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest">TL KARŞILIĞI</span>
              <span className="text-sm font-black text-blue-700">≈ {tlKarsıligi.toFixed(2)} TL</span>
            </div>
          )}
          {bankaGerekli && (
            <div>
              <label className={labelCls}>BANKA</label>
              <SearchableSelect value={banka} onChange={setBanka} options={bankaListesi} placeholder="Banka seçiniz" />
            </div>
          )}
          <div>
            <label className={labelCls}>PERSONEL</label>
            <SearchableSelect value={personeller.find(p => p.personelId === personelId)?.adSoyad || '— Seçiniz —'} onChange={handlePersonelSec} options={personelOptions} placeholder="Personel seçiniz" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className={labelCls}>TARİH</label>
              <DatePicker value={tarih} onChange={setTarih} />
            </div>
            <div>
              <label className={labelCls}>SAAT</label>
              <input type="time" value={saat} onChange={e => setSaat(e.target.value)} className={inputCls} />
            </div>
          </div>
        </div>

        <div className="px-8 py-6 flex gap-4 border-t border-gray-50 bg-gray-50/50 flex-shrink-0">
          {isDetay && onSil && (
            <button onClick={() => setConfirmAc(true)} className="flex-1 py-4 rounded-2xl border-2 border-rose-100 text-[10px] font-black text-rose-500 uppercase tracking-widest hover:bg-rose-50 transition-all active:scale-95">SİL</button>
          )}
          <button onClick={kaydet} className="flex-1 py-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-2xl text-[10px] font-black shadow-xl shadow-blue-200 transition-all hover:-translate-y-1 active:translate-y-0 tracking-widest uppercase">
            {isDetay ? '💾 GÜNCELLE' : '💾 KAYDET'}
          </button>
        </div>
      </div>
      {confirmAc && (
        <ConfirmModal mesaj="Bu gelir kaydı kalıcı olarak silinecek."
          onOnayla={() => { onSil(mevcutKayit.id); onClose(); }}
          onIptal={() => setConfirmAc(false)} />
      )}
    </div>,
    document.body
  );
}

// ── GiderEkleModal ────────────────────────────────────────────
export function GiderEkleModal({ onClose, onKaydet, onSil, mevcutKayit, giderGruplari = [], personeller = [], odemeTipleri = [], bankalar = [], paraBirimleri = [], dovizKurlari = {} }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  const isDetay = !!mevcutKayit;
  const [confirmAc, setConfirmAc] = useState(false);

  const odemeTipListesi = odemeTipleri.length ? odemeTipleri.map(o => typeof o === 'object' ? o.ad : o) : ['BANKA HAVALE', 'NAKİT', 'KREDİ KARTI', 'ÇEK'];
  const bankaListesi = bankalar.length ? bankalar.map(b => typeof b === 'object' ? b.ad : b) : [];
  const paraBirimiListesi = paraBirimleri.length ? paraBirimleri.map(p => typeof p === 'object' ? (p.tcmbKod || p.ad) : p) : ['TL', 'EURO', 'USD'];

  const [altTip, setAltTip] = useState(mevcutKayit?.altTip || 'gider');
  const [giderGrubuId, setGiderGrubuId] = useState(mevcutKayit?.giderGrubuId || null);
  const [personelHedefId, setPersonelHedefId] = useState(mevcutKayit?.personelHedefId || null);
  const [ad, setAd] = useState(mevcutKayit?.ad || '');
  const [tutar, setTutar] = useState(mevcutKayit?.tutar || 0);
  const [paraBirimi, setParaBirimi] = useState(mevcutKayit?.paraBirimi || paraBirimiListesi.find(p => p === 'TRY' || p === 'TL') || paraBirimiListesi[0] || 'TL');
  const [miktar, setMiktar] = useState(mevcutKayit?.miktar || 1);
  const [odemeTip, setOdemeTip] = useState(mevcutKayit?.odemeTip || (odemeTipListesi[0] || 'NAKİT'));
  const [banka, setBanka] = useState(mevcutKayit?.banka || '');
  const [personelId, setPersonelId] = useState(mevcutKayit?.personelId || null);
  const [tarih, setTarih] = useState(mevcutKayit?.tarih || bugunStr());
  const [saat, setSaat] = useState(mevcutKayit?.saat || saatStr());

  const kurDegeri = dovizKurlari[paraBirimi] || dovizKurlari['TL'] || 1;
  const toplam = (parseFloat(tutar) || 0) * (parseInt(miktar) || 1);
  const tlKarsıligi = toplam * kurDegeri;
  const secilenGrup = giderGruplari.find(g => g.giderGrubuId === giderGrubuId);
  const secilenHedef = personeller.find(p => p.personelId === personelHedefId);
  const grupOptions = giderGruplari.map(g => g.ad);
  const personelOptions = ['— Seçiniz —', ...personeller.map(p => p.adSoyad)];
  const bankaGerekli = odemeTip !== 'NAKİT' && bankaListesi.length > 0;

  function handleGrupSec(v) {
    const g = giderGruplari.find(g => g.ad === v);
    if (g) setGiderGrubuId(g.giderGrubuId);
  }

  function handlePersonelHedefSec(v) {
    if (v === '— Seçiniz —') { setPersonelHedefId(null); return; }
    const p = personeller.find(p => p.adSoyad === v); if (p) setPersonelHedefId(p.personelId);
  }
  function handlePersonelSec(v) {
    if (v === '— Seçiniz —') { setPersonelId(null); return; }
    const p = personeller.find(p => p.adSoyad === v); if (p) setPersonelId(p.personelId);
  }

  async function kaydet() {
    if (altTip === 'gider' && !giderGrubuId) { alert('Lütfen grup seçiniz.'); return; }
    if (!tutar || parseFloat(tutar) <= 0) { alert('Tutar giriniz.'); return; }
    try {
      await onKaydet({ id: mevcutKayit?.id, kasaGiderId: mevcutKayit?.kasaGiderId, altTip, giderGrubuId, personelHedefId, ad, tutar: parseFloat(tutar), paraBirimi, kurDegeri, miktar: parseInt(miktar) || 1, odemeTip, banka: bankaGerekli ? banka : '', personelId, tarih, saat, toplam });
      onClose();
    } catch (err) {
      alert(err?.message || 'Kaydedilemedi.');
    }
  }

  const inputCls = 'w-full border-2 border-gray-100 rounded-2xl px-5 py-3.5 text-xs font-bold text-gray-800 focus:outline-none focus:border-rose-400 focus:ring-4 focus:ring-rose-50 bg-white transition-all';
  const labelCls = 'text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1 leading-none';

  return createPortal(
    <div className="fixed inset-0 bg-black/60 z-[9999] flex items-end sm:items-center justify-center sm:p-4 backdrop-blur-md animate-fadeIn" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-xl rounded-t-[2.5rem] sm:rounded-[2.5rem] shadow-2xl flex flex-col border border-white/20 animate-scaleIn overflow-hidden" style={{ maxHeight: '95dvh' }} onClick={e => e.stopPropagation()}>
        <div className="relative flex items-center justify-between px-8 py-7 bg-gradient-to-r from-rose-500 to-red-600 text-left leading-none flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center text-2xl border border-white/30">🛒</div>
            <div>
              <h2 className="text-white font-black text-lg tracking-tight uppercase leading-none">{isDetay ? 'Gider Düzenle' : 'Gider Ekle'}</h2>
              <p className="text-white/70 text-[10px] font-bold uppercase tracking-widest mt-1.5 opacity-80 leading-none">Finansal harcama yönetimi</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white w-10 h-10 flex items-center justify-center rounded-xl hover:bg-white/10 transition-all font-bold">✕</button>
        </div>

        <div className="px-8 py-8 space-y-6 overflow-y-auto flex-1 no-scrollbar text-left leading-none">
          <div className="flex gap-4 p-1.5 bg-gray-50 rounded-2xl border-2 border-gray-100">
            {[['gider', 'Gider'], ['personel', 'Personel Gider']].map(([v, l]) => (
              <button key={v} onClick={() => setAltTip(v)}
                className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${altTip === v ? 'bg-white text-gray-800 shadow-sm border-2 border-gray-100' : 'text-gray-400 hover:text-gray-600'}`}>
                {l}
              </button>
            ))}
          </div>

          {altTip === 'gider' ? (
            <div>
              <label className={labelCls}>GRUP / KATEGORİ</label>
              <SearchableSelect value={secilenGrup?.ad || ''} onChange={handleGrupSec} options={grupOptions} placeholder="Grup seçiniz" />
            </div>
          ) : (
            <>
              <div>
                <label className={labelCls}>PERSONEL (Ödeme yapılan)</label>
                <SearchableSelect value={secilenHedef?.adSoyad || '— Seçiniz —'} onChange={handlePersonelHedefSec} options={personelOptions} placeholder="Personel seçiniz" />
              </div>
              {giderGruplari.length > 0 && (
                <div>
                  <label className={labelCls}>GRUP / KATEGORİ (Opsiyonel)</label>
                  <SearchableSelect
                    value={secilenGrup?.ad || '— Seçiniz —'}
                    onChange={v => { if (v === '— Seçiniz —') { setGiderGrubuId(null); return; } handleGrupSec(v); }}
                    options={['— Seçiniz —', ...grupOptions]}
                    placeholder="Grup seçiniz"
                  />
                </div>
              )}
            </>
          )}

          <div>
            <label className={labelCls}>AÇIKLAMA</label>
            <input value={ad} onChange={e => setAd(toTitleCase(e.target.value))} placeholder="Harcama açıklaması..." className={inputCls} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className={labelCls}>BİRİM TUTAR</label>
              <div className="relative">
                <input type="number" value={tutar} onChange={e => setTutar(e.target.value)} className={inputCls + ' pr-20'} />
                <select value={paraBirimi} onChange={e => setParaBirimi(e.target.value)} className="absolute right-2 top-2 bottom-2 rounded-xl border-none bg-gray-50 text-[10px] font-black px-2 focus:ring-0">
                  {paraBirimiListesi.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>MİKTAR</label>
              <div className="relative">
                <input type="number" min="1" value={miktar} onChange={e => setMiktar(e.target.value)} className={inputCls + ' pr-12'} />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-black text-gray-400 uppercase tracking-widest">ADET</span>
              </div>
            </div>
          </div>

          <div className="bg-rose-50 border-2 border-rose-100 rounded-[2rem] p-5 flex justify-between items-center animate-fadeIn">
            <span className="flex flex-col">
              <span className="text-[10px] font-black text-rose-600 uppercase tracking-widest leading-none">TOPLAM TUTAR</span>
              {paraBirimi !== 'TL' && paraBirimi !== 'TRY' && kurDegeri > 0 && (
                <span className="text-[9px] font-black text-blue-500 uppercase tracking-widest mt-1 opacity-70">≈ {tlKarsıligi.toFixed(2)} TL</span>
              )}
            </span>
            <span className="text-xl font-black text-rose-700 tracking-tighter leading-none">{toplam.toFixed(2)} <span className="text-[10px]">{paraBirimi}</span></span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className={labelCls}>ÖDEME TİPİ</label>
              <select value={odemeTip} onChange={e => setOdemeTip(e.target.value)} className={inputCls + ' cursor-pointer'}>
                {odemeTipListesi.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            {bankaGerekli && (
              <div>
                <label className={labelCls}>BANKA</label>
                <SearchableSelect value={banka} onChange={setBanka} options={bankaListesi} placeholder="Banka seçiniz" />
              </div>
            )}
          </div>

          <div>
            <label className={labelCls}>İŞLEMİ YAPAN PERSONEL</label>
            <SearchableSelect value={personeller.find(p => p.personelId === personelId)?.adSoyad || '— Seçiniz —'} onChange={handlePersonelSec} options={personelOptions} placeholder="Personel seçiniz" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className={labelCls}>TARİH</label>
              <DatePicker value={tarih} onChange={setTarih} />
            </div>
            <div>
              <label className={labelCls}>SAAT</label>
              <input type="time" value={saat} onChange={e => setSaat(e.target.value)} className={inputCls} />
            </div>
          </div>
        </div>

        <div className="px-8 py-6 flex gap-4 border-t border-gray-50 bg-gray-50/50 flex-shrink-0">
          {isDetay && onSil && (
            <button onClick={() => setConfirmAc(true)} className="flex-1 py-4 rounded-2xl border-2 border-rose-100 text-[10px] font-black text-rose-500 uppercase tracking-widest hover:bg-rose-50 transition-all active:scale-95">SİL</button>
          )}
          <button onClick={kaydet} className="flex-1 py-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-2xl text-[10px] font-black shadow-xl shadow-blue-200 transition-all hover:-translate-y-1 active:translate-y-0 tracking-widest uppercase">
            {isDetay ? '💾 GÜNCELLE' : '💾 KAYDET'}
          </button>
        </div>
      </div>
      {confirmAc && (
        <ConfirmModal mesaj="Bu gider kaydı kalıcı olarak silinecek."
          onOnayla={() => { onSil(mevcutKayit.id); onClose(); }}
          onIptal={() => setConfirmAc(false)} />
      )}
    </div>,
    document.body
  );
}

